'use client';

import React from 'react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';

export interface RadioOption {
  value: string;
  label: string;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
}

const Root = styled(RadixRadioGroup.Root)`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const ItemWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  color: ${(p) => p.theme.colors.neutral.ink};
  cursor: pointer;
`;

const Circle = styled(RadixRadioGroup.Item)`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.colors.neutral.lineStrong};
  background: ${(p) => p.theme.colors.neutral.panel};
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &[data-state='checked'] {
    border-color: ${(p) => p.theme.colors.brand.primary};
  }
  ${focusRing}
`;

const Dot = styled(RadixRadioGroup.Indicator)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.brand.primary};
`;

export function RadioGroup({ options, value, onChange, 'aria-label': ariaLabel }: RadioGroupProps) {
  return (
    <Root value={value} onValueChange={onChange} aria-label={ariaLabel}>
      {options.map((opt) => (
        <ItemWrapper key={opt.value}>
          <Circle value={opt.value} aria-label={opt.label}>
            <Dot />
          </Circle>
          {opt.label}
        </ItemWrapper>
      ))}
    </Root>
  );
}
